"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const date_fns_1 = require("date-fns");
/**
 * Method that comparesAscending optional dates. `null` is not ascending
 * @param left is a `string` or `null` representing the left side of the comparison
 * @param right is a `string` or `null` representing the right side of the comparison
 * @returns `0` if neither `string` is valid, `-1` if only the right value is valid, `1` if only the left value is valid,
 * `1` if the left date is after the right, `-1` if the left date is before the right date, and `0` if they are equal
 */
exports.compareAscending = (left, right) => {
    if (!left && !right) {
        return 0;
    }
    else if (!left) {
        return -1;
    }
    else if (!right) {
        return 1;
    }
    return date_fns_1.compareAsc(date_fns_1.parse(left), date_fns_1.parse(right));
};
/**
 * Method that comparesDescending optional dates. `null` is not decending
 * @param left is a `string` or `null` representing the left side of the comparison
 * @param right is a `string` or `null` representing the right side of the comparison
 * @returns `0` if neither string is valid, `-1` if only the right value is valid, `1` if only the left value is valid,
 * `-1` if the left date is after the right, `1` if the left date is before the right date, and `0` if they are equal
 */
exports.compareDescending = (left, right) => {
    if (!left && !right) {
        return 0;
    }
    else if (!left) {
        return -1;
    }
    else if (!right) {
        return 1;
    }
    return date_fns_1.compareDesc(date_fns_1.parse(left), date_fns_1.parse(right));
};
/**
 * Method that validates a date, checking that it is formatted properly and is after the input minimum date
 * @param maxDate is a string containing a date, null or undefined, the date to validate is after min date
 * @param minDate is a string containing the minimum date, null or undefined
 * @returns maxDate if it is valid and in the future of minDate; else return undefined
 */
exports.validateMaxDate = (maxDate, minDate) => {
    if (!minDate) {
        return maxDate || undefined;
    }
    else if (!maxDate) {
        return undefined;
    }
    return exports.compareDescending(maxDate, minDate) !== 1 ? maxDate : undefined;
};
/**
 * Get a set of years from an array of items
 * @param items is an array of IDatedItems
 * @returns an array of all years that are present in the input items
 */
exports.yearsForItems = (items) => {
    const years = items
        // tslint:disable-next-line:newline-per-chained-call
        .map(item => date_fns_1.getYear(item.date).toString());
    return [...new Set(years)];
};
/**
 * Sort input items with a specific year in descending order
 * @param items a set of items to sort
 * @param year, a string, to choose items by, before sorting
 * @returns a sorted, in descending order, array of all items that had the input year
 */
exports.sortedItemsForYear = (items, year) => (exports.mapDatedItemsByYear(items)
    .get(year) || []);
/**
 * Sorts the input items by their date field
 * @param items a set of items with a date string
 * @returns the items sorted in descending order, based on their date
 */
exports.sortDatedItems = (items) => (items.sort((left, right) => exports.compareDescending(left.date, right.date)));
/**
 * Sorts input payments in descending order, then map them by year and return the map
 * @param payments an array of payments to sort and map by year
 * @returns a Map of type <string, Payment[]> which contains the array of payments, sorted in descending order then mapped by year.
 * If the date is malformed - they will be bucketed as NaN.
 */
exports.mapDatedItemsByYear = (payments) => (exports.sortDatedItems(payments)
    .reduce((result, payment) => {
    const year = date_fns_1.getYear(payment.date)
        .toString();
    return result.set(year, (result.get(year) || []).concat([payment]));
}, new Map()));
/**
 * Map statements by the year of their statement end date. Statement data is currently delivered in order via
 * graphql - note that order is NOT guaranteed via this method itself, the input data must be supplied already ordered.
 * @param statements an array of statements to map by year
 * @returns a Map of type <string, DatedStatement[]> which contains all of the statements sorted by the year
 * of their statement end date. If the statementEndDate is malformed - they will be bucketed as NaN
 */
exports.mapStatementsByYear = (statements) => (statements.reduce((result, statement) => {
    const year = date_fns_1.getYear(statement.statementEndDate)
        .toString();
    return result.set(year, (result.get(year) || []).concat([statement]));
}, new Map()));
/**
 * Validates a date
 * @param date is either a formatted string date, a number, or a Date object
 * @return the validity of the date parsed out of the input string
 */
exports.isValidDate = (date) => date_fns_1.isValid(date_fns_1.parse(date));
/**
 * Validates that a given date is within the given date range (inclusive).
 * This method allows for null, undefined, or NaN range endpoints,
 * which are considered 'infinite' for comparisons. Any given
 * date will always be within an 'infinite' upper or lower bound.
 * @param date the date to test.
 * @param min the lower bound of the range.
 * @param max the upper bound of the range.
 * @return whether the input date is within a given range
 */
exports.isWithinRange = (date, min, max) => {
    if (min && max) {
        return date_fns_1.isWithinRange(date, min, max);
    }
    else if (min && !max) {
        return date_fns_1.isAfter(date, min) || date_fns_1.isEqual(date, min);
    }
    else if (!min && max) {
        return date_fns_1.isBefore(date, max) || date_fns_1.isEqual(date, max);
    }
    else { // !min && !max
        return true;
    }
};
